import { useEffect, useState } from "react";
import { OverlayTrigger, Popover } from "@rio-cloud/rio-uikit";
import CancelDispatchDialog from "./dialogs/cancelDispatchDialog";
import {
  Occurrence,
  OccurrenceDispatch,
} from "../../../../../../models/occurrence";
import { normalizeOccurrenceDispatchType, useDispatch } from "../../../../../../hooks/useDispatch";
import { getOccurrenceDispatchDocument, makeOccurrenceDispatchAvailable } from "../../../../../../api/occurrence";
import ErrorDialog from "../../../../../dialogs/ErrorDialog";
import RefusalReasonDialog from "./dialogs/refusalReasonDialog";
import CreateTripDialog from "./dialogs/createTripDialog";
import EditDispatchFieldDialog, { type EditableDispatchField } from "./dialogs/editDispatchFieldDialog";
import DispatchEditableInfo from "./dispatchEditableInfo";
import useMediaQuery from "@mui/material/useMediaQuery";
import { fetchDealershipsDnGroup } from "../../../../../../api/fetchDealerships";
import "../../../../styles.scss";
import { useIntl, IntlShape } from "react-intl";
import moment from 'moment'

const VALID_TRIP_STATUSES = [
  "TRIP_ACCEPTED",
  "TRIP_STARTED",
  "TRIP_PAUSED",
  "CHECKLIST_FILLED",
  "TRIP_FINISHED",
  "TRIP_INTERRUPTED",
  "INTERRUPTED",
] as const;

type TripStatus = (typeof VALID_TRIP_STATUSES)[number];

const TRIP_STATUS_NEUTRAL_MESSAGES: Partial<Record<TripStatus, string>> = {
  TRIP_ACCEPTED: "intl-msg:trip.accepted",
  TRIP_STARTED: "intl-msg:trip.started",
  TRIP_PAUSED: "intl-msg:trip.paused",
  CHECKLIST_FILLED: "intl-msg:trip.checklistFilled",
};

const DISPATCH_STATUS_MESSAGES: Record<string, string> = {
  "Disponível": "intl-msg:dispatchCard.status.available",
  "Indisponível para atendimento": "intl-msg:dispatchCard.status.unavailable",
  "Aguardando concessionária": "intl-msg:dispatchCard.status.awaitingDealership",
};

interface DispatchCardProps {
  status?: string;
  occurrence: Occurrence | null;
  occurrenceDispatch: OccurrenceDispatch;
  cancelDispatch?: (
    occurrence: Occurrence,
    dispatch: OccurrenceDispatch,
    reason: string,
    description: string
  ) => Promise<{
    wasCanceled: boolean;
    error?: string;
    dispatch?: OccurrenceDispatch;
  }>;
  removeDispatch?: (
    occurrence: Occurrence,
    dispatch: OccurrenceDispatch,
  ) => Promise<void>;
  persona?: "MANAGER" | "TOWER" | "CONSULTANT" | "PARTNERSHIP";
  onDispatchUpdated?: (dispatch: OccurrenceDispatch) => void;
}

const formatNameAndDate = (name?: string, date?: Date | string, intl?: IntlShape) => {
  const trimmedName = name?.trim();
  if (!trimmedName) return '';

  const parsedDate = date && moment(date).isValid() ? moment(date) : null;
  if (!parsedDate) return trimmedName;

  const formattedDate = parsedDate.format('DD/MM/YYYY');
  const formattedTime = parsedDate.format('HH:mm');

  if (!intl) {
    return `${trimmedName} ${formattedDate} às ${formattedTime}`;
  }

  return intl.formatMessage(
    { id: 'intl-msg:dispatch.cancelledByNameAndDate' },
    { name: trimmedName, date: formattedDate, time: formattedTime },
  );
};

const DispatchCard = (props: DispatchCardProps) => {
  const {
    optionsOccurrenceType,
    optionsPayer,
    dealership,
    statusColor,
    newStatus,
    setNewStatus,
    showCancelDialog,
    setShowCancelDialog,
    route,
    showCancelReasonDialog,
    setShowCancelReasonDialog,
    setNewRefusal,
    newRefusal,
    drivers,
    newDriverName,
    setNewDriverName,
  } = useDispatch(props);

  const [errorDialog, setErrorDialog] = useState<string | null>(null);

  const [showCreateTripDialog, setShowCreateTripDialog] =
    useState<boolean>(false);

  const [currentDispatch, setCurrentDispatch] = useState<OccurrenceDispatch>(props.occurrenceDispatch);
  const [editingField, setEditingField] = useState<EditableDispatchField | null>(null);

  useEffect(() => {
    setCurrentDispatch(props.occurrenceDispatch);
  }, [props.occurrenceDispatch]);

  const handleFieldSaved = (field: EditableDispatchField) => (value: string) => {
    const updated = { ...currentDispatch, [field]: value };
    setCurrentDispatch(updated);
    props.onDispatchUpdated?.(updated);
  };

  const closeFieldDialog = (field: EditableDispatchField) => () =>
    setEditingField((previous) => (previous === field ? null : previous));

  const isResponsiveView = useMediaQuery("(max-width: 1024px)");

  const handleRedirectRouting = async () => {
    const dnRes = await fetchDealershipsDnGroup(props.occurrenceDispatch.dn);
    const name = `DN ${dnRes?.id} - ${dnRes?.fantasyName?.toUpperCase()}`
    const url = `${import.meta.env.VITE_ROUTING_URL}/#/routing/trips/schedule?id=${props.occurrenceDispatch.route}&dn-group-id=${dnRes?.groupId}&dn-group-name=${name}&trip=true&clean=true`;
    window.open(url, "_blank")
  }

  const handleRedirectTrip = async () => {
    let route = '/#/routing/map/trips?trip-id='
    if (tripStatus === 'TRIP_INTERRUPTED' || tripStatus === 'INTERRUPTED') route = `/#/routing/trips/performance?tab=1&driver-id=${props.occurrenceDispatch.driver}&trip-id=`
    if (tripStatus === 'TRIP_FINISHED') route = `/#/routing/performance/list/trip?tab=1&driver-id=${props.occurrenceDispatch.driver}&trip-id=`
    
    const url = `${import.meta.env.VITE_ROUTING_URL}${route}${props.occurrenceDispatch.trip}`;
    
    window.open(url, "_blank")
  }

  const handleDownloadDocument = async () => {
    const document = await getOccurrenceDispatchDocument(props.occurrence?.vehicleChassis ?? "", props.occurrenceDispatch.id ?? "");
    if (document) {
      window.open(document, "_blank");
    }
  }

  const intl = useIntl();
  const t = (id: string, values?: Record<string, string>) => intl.formatMessage({ id: `intl-msg:dispatchCard.${id}` }, values);

  const tripStatus: TripStatus | null = VALID_TRIP_STATUSES.includes(
    props.occurrenceDispatch.tripStatus as TripStatus
  )
    ? (props.occurrenceDispatch.tripStatus as TripStatus)
    : null;
  const tripStatusNeutralMessage = tripStatus
    ? TRIP_STATUS_NEUTRAL_MESSAGES[tripStatus]
    : undefined;
  const isDispatchCanceled = newStatus === "Indisponível para atendimento";
  const newStatusLabel = DISPATCH_STATUS_MESSAGES[newStatus]
    ? intl.formatMessage({ id: DISPATCH_STATUS_MESSAGES[newStatus] })
    : newStatus;

  return (
    <>
      <div className="p-6 mt-[10px] border rounded">
        <div className="flex flex-col gap-4 pb-[5px] w-full">
          <div
            className={`flex ${
              isResponsiveView 
                ? "flex-wrap gap-4"
                : "justify-between items-center"
            } gap-[25px] ${
              newStatus === "Disponível" ||
              newStatus === "Indisponível para atendimento"
                ? "w-[100%]"
                : "w-[98%]"
            }`}
          >
            {" "}
            <div
              className={`flex ${
                isResponsiveView 
                  ? "flex-col gap-4"
                  : "flex-row justify-start gap-6"
              }`}
            >
              <div className="flex gap-6 items-start">
                <div className="flex items-center justify-center p-2 border rounded w-[40px] h-[40px]">
                  <img
                    src="/assets/images/clipboard.svg"
                    alt=""
                    style={{ width: 18, height: 18 }}
                  />
                </div>
                <div
                  className={`flex flex-col ${
                    isResponsiveView  ? "w-full" : "w-[200px]"
                  }`}
                >
                  <span className="text-color-dark font-semibold text-size-14">
                    {optionsOccurrenceType.find(
                      (option) =>
                        option.id === normalizeOccurrenceDispatchType(props.occurrenceDispatch.occurrenceType)
                    )?.label ?? t("defaultOccurrenceType")}
                  </span>
                  <span className="text-color-dark text-size-14">
                    {(() => {
                      const createdAt = new Date(props.occurrenceDispatch.createdAt ?? new Date());
                      return t("dateTime", {
                        date: intl.formatDate(createdAt, { day: "2-digit", month: "2-digit", year: "numeric" }),
                        time: intl.formatTime(createdAt, { hour: "2-digit", minute: "2-digit" }),
                      });
                    })()}
                  </span>
                </div>
              </div>

              <div
                className={`flex flex-col ${
                  isResponsiveView  ? "w-full" : "w-[250px]"
                }`}
              >
                <DispatchEditableInfo
                  label={intl.formatMessage({ id: "intl-msg:dispatch.protocol.label" })}
                  value={currentDispatch.protocol}
                  editLabel={intl.formatMessage({ id: "intl-msg:dispatch.protocol.edit" })}
                  onEdit={() => setEditingField("protocol")}
                />
              </div>

              {/* Fonte pagadora */}
              {props.persona !== "MANAGER" && (
                <div
                  className={`flex flex-col ${
                    isResponsiveView  ? "w-full" : ""
                  }`}
                >
                  <span className="text-color-dark font-semibold text-size-14">
                    {t("payer")}
                  </span>
                  <span className="text-color-dark text-size-14">
                    {optionsPayer.find(
                      (option) => option.id === props.occurrenceDispatch.payer
                    )?.label ?? ""}
                  </span>
                </div>
              )}
            </div>

            <div
              className={`flex gap-6 ${
                isResponsiveView  ? "flex-col mt-4 w-full" : "ml-auto"
              }`}
            >
              <div className="flex items-center justify-center px-[10px] gap-2 h-[48px] bg-[#F9FDFD] dark:bg-neutral-800 border-2 border-[#CEF0F3] dark:border-neutral-700 rounded">
                <span className="rioglyph rioglyph-workshop bg-[#FFFFFF] dark:bg-neutral-700 p-2 border dark:border-neutral-600 rounded"></span>
                <span className="font-semibold ml-2">{dealership?.name}</span>
              </div>
              <div className="flex items-center justify-center px-[10px] gap-2 h-[48px] bg-[#F9FDFD] dark:bg-neutral-800 border-2 border-[#CEF0F3] dark:border-neutral-700 rounded">
                <span className="rioglyph rioglyph-pin bg-[#FFFFFF] dark:bg-neutral-700 p-2 border dark:border-neutral-600 rounded"></span>
                <span className="font-semibold ml-2">
                  {[dealership?.city, dealership?.state]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end">
            {!["MANAGER"].includes(props.persona ?? "") && newStatus !== 'Indisponível para atendimento' && (
                <>
                  <div
                    className={
                      isResponsiveView 
                        ? "flex flex-col gap-2 mb-4"
                        : "hidden"
                    }
                  >
                    {newStatus === "Aguardando concessionária" && <button
                      className="btn btn-primary btn-sm"
                      onClick={async () => {
                        if (props.occurrence && props.occurrenceDispatch.id) {
                          const updated = await makeOccurrenceDispatchAvailable(
                            props.occurrence.id,
                            props.occurrenceDispatch.id
                          );
                          if (updated) setNewStatus("Disponível");
                        } else {
                          setErrorDialog(
                            t("confirmError")
                          );
                        }
                      }}
                    >
                      {t("confirm")}
                    </button>}
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowCancelDialog(true)}
                    >
                      {t("cancel")}
                    </button>

                    <div className="flex mt-2 gap-2">
                      <div className="flex items-center justify-center px-[10px] gap-2 h-[48px] bg-[#F9FDFD] dark:bg-neutral-800 border-2 border-[#CEF0F3] dark:border-neutral-700 rounded">
                        <span className="rioglyph rioglyph-workshop bg-[#FFFFFF] dark:bg-neutral-700 p-2 border dark:border-neutral-600 rounded"></span>
                        <span className="font-semibold ml-2">
                          {dealership?.name}
                        </span>
                      </div>
                      <div className="flex items-center justify-center px-[10px] gap-2 h-[48px] bg-[#F9FDFD] dark:bg-neutral-800 border-2 border-[#CEF0F3] dark:border-neutral-700 rounded">
                        <span className="rioglyph rioglyph-pin bg-[#FFFFFF] dark:bg-neutral-700 p-2 border dark:border-neutral-600 rounded"></span>
                        <span className="font-semibold ml-2">
                          {[dealership?.city, dealership?.state]
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: isResponsiveView ? "none" : "block" }}>
                    <OverlayTrigger
                      trigger="click"
                      key="left"
                      placement="left"
                      overlay={
                        <Popover
                          id="popover-positioned-left"
                          className="w-[250px]"
                        >
                          {newStatus === "Aguardando concessionária" && <div
                            className="popover-item flex items-center cursor-pointer w-[250px]"
                            onClick={async () => {
                              if (props.occurrence && props.occurrenceDispatch.id) {
                                const updated =
                                  await makeOccurrenceDispatchAvailable(
                                    props.occurrence.id,
                                    props.occurrenceDispatch.id
                                  );
                                if (updated) setNewStatus("Disponível");
                              } else {
                                setErrorDialog(
                                  t("confirmError")
                                );
                              }
                            }}
                          >
                            <span className="flex items-center justify-center text-color-darkest text-size-h4 rioglyph rioglyph-ok-sign"></span>
                            <span className="text-color-darkest text-size-14 font-semibold">
                              {t("confirm")}
                            </span>
                          </div>}
                          <div
                            className="popover-item flex items-center cursor-pointer w-[250px]"
                            onClick={() => setShowCancelDialog(true)}
                          >
                            <span className="flex items-center justify-center text-color-darkest text-size-h4 rioglyph rioglyph-eye-option"></span>
                            <span className="text-color-darkest text-size-14 font-semibold">
                              {t("cancel")}
                            </span>
                          </div>
                        </Popover>
                      }
                    >
                      <span className="rioglyph rioglyph-option-vertical cursor-pointer"></span>
                    </OverlayTrigger>
                  </div>
                </>
              )}
          </div>

          <hr className="border-gray-200 dark:border-neutral-700 my-2" />

          <div className="flex flex-col gap-4 mt-5 w-full">
            <div className={`flex ${isResponsiveView  ? "flex-col gap-4" : "flex-row items-center justify-between"} w-full`}>
              <div className={`flex ${isResponsiveView  ? "flex-col gap-4" : "flex-row items-center gap-6"}`}>
                <span className="flex items-center justify-center border dark:border-neutral-600 rounded-full bg-[#F3F4F7] dark:bg-neutral-700 w-[40px] h-[40px]">
                  <span className="rioglyph rioglyph-truck text-[#667085] dark:text-neutral-300 text-size-14"></span>
                </span>

                <div className={`flex flex-col ${isResponsiveView  ? "w-full" : "w-[200px]"}`}>
                  <div className={`flex ${isResponsiveView  ? "flex-col" : "gap-[20px]"} mb-1`}>
                    <span className="text-size-14 text-color-dark">
                      <span className="font-semibold">{t("model")}:</span>{" "}
                      {props.occurrence?.vehicleModel || "-"}
                    </span>
                    <span className="text-size-14 text-color-dark">
                      <span className="font-semibold">{t("plate")}:</span>{" "}
                      {props.occurrence?.vehicleLicensePlate || "-"}
                    </span>
                  </div>
                  <span className="text-size-14 text-color-dark">
                    <span className="font-semibold">{t("chassis")}:</span>{" "}
                    {props.occurrence?.vehicleChassis || "-"}
                  </span>
                </div>

                <div className={`flex flex-col ${isResponsiveView  ? "w-full" : "w-[250px]"}`}>
                  <span className="text-size-14 font-semibold text-color-dark">
                    {t("vehicleLocation")}
                  </span>
                  <span className="text-size-14 text-color-dark whitespace-nowrap overflow-hidden text-ellipsis route-destination">
                    {props.occurrenceDispatch.destinationLocation || route?.destination || "-"}
                  </span>
                </div>

                <div className={`flex flex-col ${isResponsiveView  ? "w-full" : ""}`}>
                  <span className="text-size-14 font-semibold text-color-dark">
                    {t("mechanic")}
                  </span>
                  <span className="text-size-14">{newDriverName ?? "-"}</span>
                </div>
              </div>

              <div className={`flex ${isResponsiveView  ? "flex-col gap-4 w-full" : "flex-col items-end gap-2"}`}>
                {newStatus === "Indisponível para atendimento" && (
                  <div
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => setShowCancelReasonDialog(true)}
                  >
                    <span className="rioglyph rioglyph-clipboard text-[18px] text-[#A7AFBB] dark:text-neutral-400"></span>
                    <span className="text-[#2A3740] dark:text-neutral-200 underline text-size-14">
                      {t("viewOpinion")}
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-4 mb-4">
                  {!tripStatus || isDispatchCanceled ?
                    <span
                      className={`px-[10px] py-[4px] bg-[#F5F8FB] dark:bg-neutral-800 border rounded-full text-size-14 ${isResponsiveView  ? "text-center": ""}`}
                      style={{
                        borderColor: statusColor(newStatus),
                        color: statusColor(newStatus),
                      }}
                    >
                      {newStatusLabel}
                    </span> : null
                  }
                  
                  {tripStatusNeutralMessage && !isDispatchCanceled ? 
                    <span className={`px-[10px] py-[4px] bg-[#F5F8FB] dark:bg-neutral-800 border rounded-full text-size-14 ${isResponsiveView  ? "text-center": ""}`}>
                      {intl.formatMessage({ id: tripStatusNeutralMessage })}
                    </span> : null
                  }

                  {(tripStatus === 'TRIP_INTERRUPTED' || tripStatus === 'INTERRUPTED') && !isDispatchCanceled ?
                    <span className={`px-[10px] py-[4px] bg-[#FEF2F2] text-[#DC2626] border border-[#FCA5A5] dark:bg-red-900/30 dark:text-red-300 dark:border-red-800 rounded-full text-size-14 ${isResponsiveView  ? "text-center": ""}`}>
                      {intl.formatMessage({ id: 'intl-msg:trip.interrupted' })}
                    </span> : null
                  }

                  {tripStatus === 'TRIP_FINISHED' && !isDispatchCanceled ? 
                    <span className={`px-[10px] py-[4px] bg-[#ECFDF3] text-[#067647] border border-[#ABEFC6] dark:bg-green-900/30 dark:text-green-300 dark:border-green-800 rounded-full text-size-14 ${isResponsiveView  ? "text-center": ""}`}>
                      {intl.formatMessage({ id: 'intl-msg:trip.finished' })}
                    </span> : null
                  }
                </div>
                
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center cursor-pointer  mx-4" onClick={handleDownloadDocument}>
                    <span className="rioglyph rioglyph-icon-off rioglyph-paper-clip mx-2" />
                    <span className="text-size-14 text-info underline">{t("dossier")}</span>
                  </div>

                  {newStatus === "Disponível" &&
                    !["MANAGER"].includes(props.persona ?? "") && (
                      <button
                        className={`btn btn-default btn-sm ${isResponsiveView  ? "w-full" : "w-auto"}`}
                        onClick={handleRedirectRouting}
                      >
                        {props.occurrenceDispatch.occurrenceType?.includes("Socorro") ||
                        props.occurrenceDispatch.occurrenceType?.includes("ASSISTANCE")
                          ? t("assignMechanic")
                          : t("assignTowTruck")}
                      </button>
                    )
                  }

                  {
                    props.occurrenceDispatch.trip ? 
                      <button
                        className={`btn btn-info btn-sm ${isResponsiveView  ? "w-full" : "w-auto"}`}
                        onClick={handleRedirectTrip}
                      >
                        {t("followTrip")}
                      </button> : null
                  }
                </div>
              </div>
            </div>
          </div>
        </div>    
      </div>

      {props.occurrenceDispatch?.cancelledBy ? (
        <div className="margin-top-10">
        <span className="text-color-dark px-2 italic">
          <span className="">
            {intl.formatMessage({ id: "intl-msg:dispatch.cancelledBy" })}{" "}
          </span>
          {formatNameAndDate(props.occurrenceDispatch?.cancelledBy, props.occurrenceDispatch?.updatedAt, intl)}
        </span>
      </div>
      ): null}

      <CancelDispatchDialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        occurrence={props.occurrence}
        occurrenceDispatch={props.occurrenceDispatch}
        cancelDispatch={props.cancelDispatch}
        updateStatus={(status, reason, description) => {
          setNewStatus(status);
          setNewRefusal({ reason, description });
        }}
      />

      <RefusalReasonDialog
        show={showCancelReasonDialog}
        onClose={() => setShowCancelReasonDialog(false)}
        newRefusal={newRefusal ?? { reason: "", description: "" }}
        cancelledBy={formatNameAndDate(props.occurrenceDispatch?.cancelledBy, props.occurrenceDispatch?.updatedAt, intl)}
      />

      <ErrorDialog
        show={!!errorDialog}
        onClose={() => setErrorDialog(null)}
        title={t("errorTitle")}
        body={errorDialog}
      />

      <EditDispatchFieldDialog
        field="protocol"
        show={editingField === "protocol"}
        onClose={closeFieldDialog("protocol")}
        occurrence={props.occurrence}
        occurrenceDispatch={currentDispatch}
        onSaved={handleFieldSaved("protocol")}
      />

      <CreateTripDialog
        show={showCreateTripDialog}
        onClose={() => setShowCreateTripDialog(false)}
        occurrence={props.occurrence}
        occurrenceDispatch={props.occurrenceDispatch}
        routeId={props.occurrenceDispatch.route ?? ""}
        onTripCreated={(driverName: string) => {
          setNewStatus("Disponível");
          setNewDriverName(driverName);
        }}
        drivers={drivers}
      />
    </>
  );
};

export default DispatchCard;
